import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { classService, teacherService } from '../../services/index';

const PtmManagement = ({ token, orgId, teachers: propTeachers = [], classes: propClasses = [] }) => {
  const [events, setEvents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Real Database Lists
  const [realClasses, setRealClasses] = useState([]);
  const [realTeachers, setRealTeachers] = useState([]);

  // Wizard Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Step 1: Event Details Form
  const [title, setTitle] = useState('Parent-Teacher Meeting 2026');
  const [academicYear, setAcademicYear] = useState('2026-27');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(10);
  const [breakDurationMinutes, setBreakDurationMinutes] = useState(0);

  // Step 2: Class & Section Form
  const [selectedClass, setSelectedClass] = useState('ALL');
  const [selectedSection, setSelectedSection] = useState('ALL');

  // Step 3: Teacher Selection Form
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);

  // Step 4: Booking Rules Form
  const [oneBookingPerStudent, setOneBookingPerStudent] = useState(true);
  const [allowCancelReschedule, setAllowCancelReschedule] = useState(true);
  const [bookingOpenDate, setBookingOpenDate] = useState('');
  const [bookingCloseDate, setBookingCloseDate] = useState('');
  const [adminInstructions, setAdminInstructions] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // Dashboard Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const getActiveToken = () => token || localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('superAdminToken');

  // Fetch Dashboard Data & Real Organization Classes/Teachers
  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const activeToken = getActiveToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/ptm/dashboard`, {
        headers: {
          'Authorization': `Bearer ${activeToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSlots(data.slots || []);
        if (data.events) setEvents(data.events);
      } else {
        setError(data.error || 'Failed to load PTM slots.');
      }
    } catch (e) {
      setError('Connection error loading PTM data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealClassesAndTeachers = async () => {
    try {
      // 1. Classes
      let cList = Array.isArray(propClasses) && propClasses.length > 0 ? propClasses : [];
      if (cList.length === 0) {
        try {
          const classRes = await classService.getAllClasses();
          if (Array.isArray(classRes)) cList = classRes;
          else if (classRes && Array.isArray(classRes.classes)) cList = classRes.classes;
        } catch (e) {
          console.warn('classService.getAllClasses error:', e.message);
        }
      }
      setRealClasses(cList);

      // 2. Teachers
      let tList = Array.isArray(propTeachers) && propTeachers.length > 0 ? propTeachers : [];
      if (tList.length === 0) {
        try {
          const teacherRes = await teacherService.getTeachers();
          if (Array.isArray(teacherRes)) tList = teacherRes;
          else if (teacherRes && Array.isArray(teacherRes.teachers)) tList = teacherRes.teachers;
          else if (teacherRes && Array.isArray(teacherRes.data)) tList = teacherRes.data;
        } catch (e) {
          console.warn('teacherService.getTeachers error:', e.message);
        }
      }

      // If still empty, attempt direct API fetch
      if (tList.length === 0) {
        try {
          const activeToken = getActiveToken();
          const rawRes = await fetch(`${API_BASE_URL}/api/teacher/all`, {
            headers: { 'Authorization': `Bearer ${activeToken}` }
          });
          const rawData = await rawRes.json();
          if (Array.isArray(rawData)) tList = rawData;
          else if (rawData && Array.isArray(rawData.teachers)) tList = rawData.teachers;
          else if (rawData && Array.isArray(rawData.data)) tList = rawData.data;
        } catch (e) {
          console.warn('Direct fetch /api/teacher/all error:', e.message);
        }
      }

      const activeOnly = (tList || []).filter(t => (t.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
      setRealTeachers(activeOnly);
      setSelectedTeacherIds(activeOnly.map(t => t.teacherId || t.id));
    } catch (err) {
      console.error('Error loading real classes/teachers:', err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchRealClassesAndTeachers();
  }, [token, propTeachers, propClasses]);

  // Calculate Slots count for Preview (Step 5)
  const calculateSlotsPerTeacher = () => {
    try {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const totalStartMin = sh * 60 + sm;
      const totalEndMin = eh * 60 + em;
      const totalAvailableMin = totalEndMin - totalStartMin;
      if (totalAvailableMin <= 0) return 0;
      const unitTime = Number(slotDurationMinutes) + Number(breakDurationMinutes);
      if (unitTime <= 0) return 0;
      return Math.floor(totalAvailableMin / unitTime);
    } catch (e) {
      return 0;
    }
  };

  const slotsPerTeacher = calculateSlotsPerTeacher();
  const totalCalculatedSlots = selectedTeacherIds.length * slotsPerTeacher;

  // Toggle Teacher Selection
  const toggleTeacher = (teacherId) => {
    setSelectedTeacherIds(prev =>
      prev.includes(teacherId) ? prev.filter(id => id !== teacherId) : [...prev, teacherId]
    );
  };

  const toggleSelectAllTeachers = () => {
    if (selectedTeacherIds.length === realTeachers.length) {
      setSelectedTeacherIds([]);
    } else {
      setSelectedTeacherIds(realTeachers.map(t => t.teacherId || t.id));
    }
  };

  // Submit Publish Event
  const handlePublishPtmEvent = async () => {
    setSubmitting(true);
    try {
      const activeToken = getActiveToken();
      const payload = {
        title,
        academicYear,
        meetingDate,
        startTime,
        endTime,
        slotDurationMinutes: Number(slotDurationMinutes),
        breakDurationMinutes: Number(breakDurationMinutes),
        className: selectedClass === 'ALL' ? 'All Classes' : selectedClass,
        section: selectedSection === 'ALL' ? 'All Sections' : selectedSection,
        teacherIds: selectedTeacherIds,
        oneBookingPerStudent,
        allowCancelReschedule,
        bookingOpenDate: bookingOpenDate || null,
        bookingCloseDate: bookingCloseDate || null,
        adminInstructions
      };

      const res = await fetch(`${API_BASE_URL}/api/admin/ptm/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${activeToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setShowCreateModal(false);
        setWizardStep(1);
        alert(`Successfully published PTM Event!\nGenerated ${data.slotsCount || 0} distinct slots across ${data.selectedTeachersCount || 0} active teachers.`);
        fetchDashboardData();
      } else {
        alert(data.error || 'Failed to create PTM event');
      }
    } catch (err) {
      alert('Error creating PTM event: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this available slot?')) return;
    try {
      const activeToken = getActiveToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/ptm/slots/${slotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${activeToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSlots(prev => prev.filter(s => s.id !== slotId));
      } else {
        alert(data.error || 'Failed to delete slot');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleClearAvailableSlots = async () => {
    if (!window.confirm(`Are you sure you want to delete all ${availableSlotsCount} unbooked (AVAILABLE) slots? Booked parent meetings will NOT be deleted.`)) return;
    try {
      const activeToken = getActiveToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/ptm/clear-available`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${activeToken}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert('All unbooked slots cleared successfully.');
        fetchDashboardData();
      } else {
        alert(data.error || 'Failed to clear slots');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const filteredSlots = slots.filter((slot) => {
    const matchesStatus = statusFilter === 'ALL' || slot.slotStatus === statusFilter;
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      (slot.teacherName && slot.teacherName.toLowerCase().includes(searchLower)) ||
      (slot.parentName && slot.parentName.toLowerCase().includes(searchLower)) ||
      (slot.studentName && slot.studentName.toLowerCase().includes(searchLower)) ||
      (slot.className && slot.className.toLowerCase().includes(searchLower));
    return matchesStatus && matchesSearch;
  });

  // Sort BOOKED appointments to the very top so they are never lost
  const sortedSlots = [...filteredSlots].sort((a, b) => {
    const statusPriority = { BOOKED: 1, COMPLETED: 2, NO_SHOW: 3, AVAILABLE: 4, CANCELLED: 5 };
    const pA = statusPriority[a.slotStatus] || 99;
    const pB = statusPriority[b.slotStatus] || 99;
    if (pA !== pB) return pA - pB;
    if (a.meetingDate !== b.meetingDate) return a.meetingDate.localeCompare(b.meetingDate);
    return (a.startTime || '').localeCompare(b.startTime || '');
  });

  const totalSlotsCount = slots.length;
  const bookedSlotsCount = slots.filter((s) => s.slotStatus === 'BOOKED').length;
  const availableSlotsCount = slots.filter((s) => s.slotStatus === 'AVAILABLE').length;
  const completedSlotsCount = slots.filter((s) => s.slotStatus === 'COMPLETED').length;
  const noShowSlotsCount = slots.filter((s) => s.slotStatus === 'NO_SHOW').length;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'inherit' }}>
      {/* Header Banner */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        padding: '24px 28px',
        borderRadius: '16px',
        boxShadow: '0 10px 25px -5px rgba(30, 58, 138, 0.2)',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>🤝</span> Parent-Teacher Meeting (PTM) Management
          </h2>
          <p style={{ margin: '6px 0 0 0', fontSize: '13.5px', color: '#dbeafe', opacity: 0.9 }}>
            Configure PTM events, auto-generate teacher-specific slots, and monitor real-time parent bookings.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {availableSlotsCount > 0 && (
            <button
              onClick={handleClearAvailableSlots}
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                color: '#fee2e2',
                border: '1px solid rgba(254, 202, 202, 0.5)',
                fontWeight: '700',
                padding: '10px 18px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🗑️</span> Clear Unbooked ({availableSlotsCount})
            </button>
          )}
          <button
            onClick={() => { setWizardStep(1); setShowCreateModal(true); }}
            style={{
              backgroundColor: '#ffffff',
              color: '#1e3a8a',
              fontWeight: '700',
              padding: '11px 22px',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13.5px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
          >
            + Create PTM Event
          </button>
        </div>
      </div>

      {/* Summary Metric Cards (Interactive click-to-filter) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div
          onClick={() => setStatusFilter('ALL')}
          style={{
            backgroundColor: '#ffffff',
            padding: '18px 20px',
            borderRadius: '12px',
            borderLeft: '4px solid #64748b',
            border: statusFilter === 'ALL' ? '2px solid #1e3a8a' : '1px solid #e2e8f0',
            borderLeftWidth: '4px',
            borderLeftColor: '#64748b',
            cursor: 'pointer',
            boxShadow: statusFilter === 'ALL' ? '0 4px 12px rgba(30, 58, 138, 0.15)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Slots</span>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', marginTop: '4px' }}>{totalSlotsCount}</div>
        </div>

        <div
          onClick={() => setStatusFilter('AVAILABLE')}
          style={{
            backgroundColor: '#ffffff',
            padding: '18px 20px',
            borderRadius: '12px',
            borderLeft: '4px solid #2563eb',
            border: statusFilter === 'AVAILABLE' ? '2px solid #2563eb' : '1px solid #e2e8f0',
            borderLeftWidth: '4px',
            borderLeftColor: '#2563eb',
            cursor: 'pointer',
            boxShadow: statusFilter === 'AVAILABLE' ? '0 4px 12px rgba(37, 99, 235, 0.15)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Available</span>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#1d4ed8', marginTop: '4px' }}>{availableSlotsCount}</div>
        </div>

        <div
          onClick={() => setStatusFilter('BOOKED')}
          style={{
            backgroundColor: '#ffffff',
            padding: '18px 20px',
            borderRadius: '12px',
            borderLeft: '4px solid #d97706',
            border: statusFilter === 'BOOKED' ? '2px solid #d97706' : '1px solid #e2e8f0',
            borderLeftWidth: '4px',
            borderLeftColor: '#d97706',
            cursor: 'pointer',
            boxShadow: statusFilter === 'BOOKED' ? '0 4px 12px rgba(217, 119, 6, 0.2)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Booked 📌</span>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#b45309', marginTop: '4px' }}>{bookedSlotsCount}</div>
        </div>

        <div
          onClick={() => setStatusFilter('COMPLETED')}
          style={{
            backgroundColor: '#ffffff',
            padding: '18px 20px',
            borderRadius: '12px',
            borderLeft: '4px solid #16a34a',
            border: statusFilter === 'COMPLETED' ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderLeftWidth: '4px',
            borderLeftColor: '#16a34a',
            cursor: 'pointer',
            boxShadow: statusFilter === 'COMPLETED' ? '0 4px 12px rgba(22, 163, 74, 0.15)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Completed</span>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#15803d', marginTop: '4px' }}>{completedSlotsCount}</div>
        </div>

        <div
          onClick={() => setStatusFilter('NO_SHOW')}
          style={{
            backgroundColor: '#ffffff',
            padding: '18px 20px',
            borderRadius: '12px',
            borderLeft: '4px solid #dc2626',
            border: statusFilter === 'NO_SHOW' ? '2px solid #dc2626' : '1px solid #e2e8f0',
            borderLeftWidth: '4px',
            borderLeftColor: '#dc2626',
            cursor: 'pointer',
            boxShadow: statusFilter === 'NO_SHOW' ? '0 4px 12px rgba(220, 38, 38, 0.15)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em' }}>No Show</span>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#b91c1c', marginTop: '4px' }}>{noShowSlotsCount}</div>
        </div>
      </div>

      {/* Filters & Search Controls */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '14px 20px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '14px'
      }}>
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
          {['ALL', 'AVAILABLE', 'BOOKED', 'COMPLETED', 'NO_SHOW'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: '600',
                cursor: 'pointer',
                border: 'none',
                backgroundColor: statusFilter === st ? '#1e3a8a' : '#f1f5f9',
                color: statusFilter === st ? '#ffffff' : '#475569',
                transition: 'all 0.15s ease'
              }}
            >
              {st}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="🔍 Search teacher, parent, or student..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 14px',
            fontSize: '13px',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            width: '280px',
            outline: 'none'
          }}
        />
      </div>

      {/* Slots Table */}
      {loading ? (
        <div style={{ backgroundColor: '#ffffff', padding: '60px', textAlign: 'center', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#64748b' }}>
          <div style={{ fontSize: '28px', marginBottom: '10px' }}>⟳</div>
          <div>Loading PTM Schedule...</div>
        </div>
      ) : error ? (
        <div style={{ backgroundColor: '#ffffff', padding: '40px', textAlign: 'center', borderRadius: '12px', border: '1px solid #fca5a5', color: '#b91c1c' }}>
          <div style={{ fontWeight: '700', fontSize: '15px' }}>{error}</div>
          <button
            onClick={fetchDashboardData}
            style={{ marginTop: '12px', padding: '6px 16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
          >
            Retry
          </button>
        </div>
      ) : sortedSlots.length === 0 ? (
        <div style={{ backgroundColor: '#ffffff', padding: '60px', textAlign: 'center', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#64748b' }}>
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📅</div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#334155' }}>No PTM slots match your criteria</div>
          <p style={{ fontSize: '13px', marginTop: '4px' }}>Click "+ Create PTM Event" to launch the 5-Step PTM Creation Wizard.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Date & Time</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Teacher</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Class & Sec</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Student</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Parent</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Status</th>
                <th style={{ padding: '14px 18px', fontWeight: '700' }}>Notes</th>
                <th style={{ padding: '14px 18px', fontWeight: '700', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedSlots.map((slot, index) => {
                const isBooked = slot.slotStatus === 'BOOKED';
                let badgeBg = '#e0f2fe';
                let badgeColor = '#0369a1';
                let badgeBorder = '#bae6fd';

                if (isBooked) {
                  badgeBg = '#fef3c7';
                  badgeColor = '#b45309';
                  badgeBorder = '#fde68a';
                } else if (slot.slotStatus === 'COMPLETED') {
                  badgeBg = '#dcfce7';
                  badgeColor = '#15803d';
                  badgeBorder = '#86efac';
                } else if (slot.slotStatus === 'NO_SHOW') {
                  badgeBg = '#ffe4e6';
                  badgeColor = '#be123c';
                  badgeBorder = '#fecdd3';
                }

                return (
                  <tr
                    key={slot.id}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isBooked ? '#fffbeb' : (index % 2 === 0 ? '#ffffff' : '#fafafa'),
                      borderLeft: isBooked ? '4px solid #f59e0b' : 'none',
                    }}
                  >
                    <td style={{ padding: '14px 18px', fontWeight: '700', color: '#0f172a' }}>
                      {slot.meetingDate}
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginTop: '2px' }}>{slot.startTime} - {slot.endTime}</div>
                    </td>
                    <td style={{ padding: '14px 18px', fontWeight: '700', color: '#1e293b' }}>
                      👨‍🏫 {slot.teacherName || 'Teacher'}
                    </td>
                    <td style={{ padding: '14px 18px', color: '#334155' }}>
                      <strong>{slot.className}</strong> <span style={{ fontSize: '12px', color: '#64748b' }}>(Sec {slot.section})</span>
                    </td>
                    <td style={{ padding: '14px 18px', fontWeight: '600', color: '#0f172a' }}>
                      {slot.studentName ? `👨‍🎓 ${slot.studentName}` : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      {slot.parentName ? (
                        <div>
                          <div style={{ fontWeight: '600', color: '#334155' }}>{slot.parentName}</div>
                          {slot.parentPhone && <div style={{ fontSize: '11px', color: '#64748b' }}>📞 {slot.parentPhone}</div>}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 18px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '9999px',
                        fontSize: '11px',
                        fontWeight: '800',
                        backgroundColor: badgeBg,
                        color: badgeColor,
                        border: `1px solid ${badgeBorder}`
                      }}>
                        {slot.slotStatus}
                      </span>
                    </td>
                    <td style={{ padding: '14px 18px', fontSize: '12px', color: '#475569', maxWidth: '180px' }}>
                      {slot.teacherNotes || slot.parentNotes || '—'}
                    </td>
                    <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                      {slot.slotStatus === 'AVAILABLE' ? (
                        <button
                          onClick={() => handleDeleteSlot(slot.id)}
                          style={{
                            padding: '4px 9px',
                            backgroundColor: '#fee2e2',
                            color: '#b91c1c',
                            border: '1px solid #fca5a5',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '700',
                            transition: 'all 0.15s ease'
                          }}
                          title="Delete unbooked slot"
                        >
                          🗑️ Delete
                        </button>
                      ) : isBooked ? (
                        <span style={{ fontSize: '11.5px', color: '#b45309', fontWeight: '700' }}>
                          📌 Confirmed
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 5-STEP WIZARD MODAL */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            width: '100%',
            maxWidth: '650px',
            overflow: 'hidden',
            border: '1px solid #cbd5e1'
          }}>
            {/* Header with Step Progress Indicator */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              color: 'white',
              padding: '20px 24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>
                  Create PTM Event & Teacher Slots Wizard
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', opacity: 0.8 }}
                >
                  ×
                </button>
              </div>

              {/* Wizard Steps Stepper */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', position: 'relative' }}>
                {[
                  { num: 1, label: 'Event' },
                  { num: 2, label: 'Classes' },
                  { num: 3, label: 'Teachers' },
                  { num: 4, label: 'Rules' },
                  { num: 5, label: 'Publish' },
                ].map((s) => (
                  <div key={s.num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: wizardStep >= s.num ? '#ffffff' : 'rgba(255, 255, 255, 0.25)',
                      color: wizardStep >= s.num ? '#1e3a8a' : '#ffffff',
                      fontWeight: '800',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      {wizardStep > s.num ? '✓' : s.num}
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: '600', marginTop: '4px', opacity: wizardStep >= s.num ? 1 : 0.7 }}>
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* STEP 1: EVENT DETAILS */}
            {wizardStep === 1 && (
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e3a8a' }}>Step 1: Event Schedule & Timing</h4>
                
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>PTM Event Title *</label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Mid-Term Parent-Teacher Conference"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Academic Year *</label>
                    <input
                      type="text"
                      required
                      value={academicYear}
                      onChange={(e) => setAcademicYear(e.target.value)}
                      placeholder="e.g. 2026-27"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Meeting Date *</label>
                    <input
                      type="date"
                      required
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Start Time *</label>
                    <input
                      type="time"
                      required
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>End Time *</label>
                    <input
                      type="time"
                      required
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Slot Duration *</label>
                    <select
                      value={slotDurationMinutes}
                      onChange={(e) => setSlotDurationMinutes(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', backgroundColor: '#ffffff' }}
                    >
                      <option value={5}>5 Minutes</option>
                      <option value={10}>10 Minutes</option>
                      <option value={15}>15 Minutes</option>
                      <option value={20}>20 Minutes</option>
                      <option value={30}>30 Minutes</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Break Duration</label>
                    <select
                      value={breakDurationMinutes}
                      onChange={(e) => setBreakDurationMinutes(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', backgroundColor: '#ffffff' }}
                    >
                      <option value={0}>0 Minutes (No break)</option>
                      <option value={5}>5 Minutes</option>
                      <option value={10}>10 Minutes</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: CLASS & SECTION */}
            {wizardStep === 2 && (
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e3a8a' }}>Step 2: Target Classes & Sections</h4>
                <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b' }}>
                  Select the real organization classes and sections eligible for parent slot bookings.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Class Name *</label>
                    <select
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', backgroundColor: '#ffffff' }}
                    >
                      <option value="ALL">All Classes</option>
                      {realClasses.map((c) => (
                        <option key={c.classId || c.id || c.className} value={c.className || c.name}>
                          {c.className || c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Section *</label>
                    <select
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', backgroundColor: '#ffffff' }}
                    >
                      <option value="ALL">All Sections</option>
                      {['A', 'B', 'C', 'D', 'E'].map(sec => (
                        <option key={sec} value={sec}>Section {sec}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ background: '#eff6ff', padding: '14px', borderRadius: '10px', border: '1px solid #bfdbfe', fontSize: '12.5px', color: '#1e40af' }}>
                  ℹ️ <strong>Database Scope:</strong> Selected scope will be set to: <strong>{selectedClass === 'ALL' ? 'All Classes' : selectedClass}</strong> (Section {selectedSection === 'ALL' ? 'All Sections' : selectedSection}).
                </div>
              </div>
            )}

            {/* STEP 3: TEACHER ASSIGNMENT */}
            {wizardStep === 3 && (
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e3a8a' }}>Step 3: Assign Active Teachers</h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Each selected teacher will receive their OWN independent schedule of slots.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleSelectAllTeachers}
                    style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    {selectedTeacherIds.length === realTeachers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px' }}>
                  {realTeachers.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      No active teachers found in organization.
                    </div>
                  ) : (
                    realTeachers.map((t) => {
                      const tid = t.teacherId || t.id;
                      const isSelected = selectedTeacherIds.includes(tid);
                      return (
                        <div
                          key={tid}
                          onClick={() => toggleTeacher(tid)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            marginBottom: '4px',
                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                            border: `1px solid ${isSelected ? '#93c5fd' : '#f1f5f9'}`,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>
                                {t.teacherName || t.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                ID: {t.teacherId} • {t.designation || 'Faculty Member'}
                              </div>
                            </div>
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#15803d', backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '4px' }}>
                            ACTIVE
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* STEP 4: BOOKING RULES */}
            {wizardStep === 4 && (
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e3a8a' }}>Step 4: Booking Governance & Rules</h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={oneBookingPerStudent}
                      onChange={(e) => setOneBookingPerStudent(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>Enforce strictly <strong>One Booking per Student</strong> across entire event</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={allowCancelReschedule}
                      onChange={(e) => setAllowCancelReschedule(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>Allow parents to cancel or reschedule existing bookings</span>
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Booking Open Date/Time</label>
                    <input
                      type="datetime-local"
                      value={bookingOpenDate}
                      onChange={(e) => setBookingOpenDate(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12.5px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Booking Close Date/Time</label>
                    <input
                      type="datetime-local"
                      value={bookingCloseDate}
                      onChange={(e) => setBookingCloseDate(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12.5px' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Admin Instructions / Parent Notes</label>
                  <textarea
                    rows={2}
                    value={adminInstructions}
                    onChange={(e) => setAdminInstructions(e.target.value)}
                    placeholder="Instructions shown to parents when booking slots..."
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {/* STEP 5: PREVIEW & PUBLISH */}
            {wizardStep === 5 && (
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e3a8a' }}>Step 5: Dynamic Slot Calculation & Publish</h4>

                <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                  <div><strong>Event Title:</strong> {title}</div>
                  <div><strong>Academic Year:</strong> {academicYear}</div>
                  <div><strong>Meeting Date:</strong> {meetingDate}</div>
                  <div><strong>Time Window:</strong> {startTime} - {endTime}</div>
                  <div><strong>Slot Duration:</strong> {slotDurationMinutes} Mins</div>
                  <div><strong>Target Scope:</strong> {selectedClass === 'ALL' ? 'All Classes' : selectedClass}</div>
                </div>

                {/* Calculation Summary Box */}
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: '12px',
                  padding: '18px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Calculated Slot Matrix
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#15803d', marginTop: '6px' }}>
                    {selectedTeacherIds.length} Selected Teachers × {slotsPerTeacher} Slots / Teacher = <strong>{totalCalculatedSlots} Total Slots</strong>
                  </div>
                  <div style={{ fontSize: '12px', color: '#166534', marginTop: '4px' }}>
                    Each selected active teacher will receive their OWN independent timetable of slots.
                  </div>
                </div>
              </div>
            )}

            {/* Footer Wizard Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => {
                  if (wizardStep > 1) setWizardStep(wizardStep - 1);
                  else setShowCreateModal(false);
                }}
                style={{ padding: '9px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              >
                {wizardStep === 1 ? 'Cancel' : '‹ Back'}
              </button>

              {wizardStep < 5 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (wizardStep === 1 && (!title || !meetingDate || !startTime || !endTime)) {
                      alert('Please fill in all required event details');
                      return;
                    }
                    if (wizardStep === 3 && selectedTeacherIds.length === 0) {
                      alert('Please select at least one active teacher for slot generation');
                      return;
                    }
                    setWizardStep(wizardStep + 1);
                  }}
                  style={{ padding: '9px 22px', backgroundColor: '#1e3a8a', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Next Step ›
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePublishPtmEvent}
                  disabled={submitting || totalCalculatedSlots === 0}
                  style={{
                    padding: '9px 24px',
                    backgroundColor: '#16a34a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    fontWeight: '800',
                    cursor: (submitting || totalCalculatedSlots === 0) ? 'not-allowed' : 'pointer',
                    opacity: (submitting || totalCalculatedSlots === 0) ? 0.7 : 1,
                    boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.2)'
                  }}
                >
                  {submitting ? 'Generating Slots...' : '🚀 Publish PTM Event'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PtmManagement;
